<template>
  <div class="seller-profile-root" style="background:var(--fm-bg2,#0A0A0A)!important;min-height:100vh;">
    <head>
      <meta charset="utf-8" />
      <title>FLOCKR | {{ seller.display_name || 'Seller Profile' }}</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <meta content="View seller profile on Flockr" name="description" />
      <meta content="Flockr" name="author" />
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body :data-sidebar="darkMode ? 'dark' : 'light'">
    <div id="layout-wrapper">

      <!-- ========== TOP BAR ========== -->
      <nav class="sp-topbar">
        <div class="container d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center" style="gap:16px;">
            <a href="javascript:void(0)" @click="$router.push('/')">
              <img src="assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" />
            </a>
            <button class="btn btn-sm btn-link text-muted p-0" @click="$router.back()">
              <i class="mdi mdi-arrow-left me-1"></i> Back
            </button>
          </div>
          <div class="d-flex align-items-center" style="gap:12px;">
            <a href="javascript:void(0)" @click="$router.push('/marketplace')" class="text-muted" style="font-size:13px;text-decoration:none;">
              <i class="mdi mdi-storefront-outline me-1"></i> Marketplace
            </a>
          </div>
        </div>
      </nav>

      <!-- ========== LOADING ========== -->
      <div v-if="loading" class="text-center py-5" style="padding-top:100px!important;">
        <div class="spinner-border text-warning" role="status"></div>
        <p class="mt-3" style="color:var(--fm-s,#6B6356);">Loading seller profile...</p>
      </div>

      <!-- ========== NOT FOUND ========== -->
      <div v-else-if="!seller.id" class="text-center py-5" style="padding-top:100px!important;">
        <i class="mdi mdi-account-off-outline" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
        <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">Seller Not Found</h5>
        <p style="color:var(--fm-s,#6B6356);">This seller profile doesn't exist or has been removed.</p>
        <button class="btn btn-warning btn-sm mt-2" @click="$router.push('/marketplace')">Browse Marketplace</button>
      </div>

      <!-- ========== SELLER PROFILE ========== -->
      <div v-else class="container sp-container">

        <!-- ===== HEADER CARD ===== -->
        <div class="sp-header-card" style="background:var(--fm-bg1,#141414)!important;">
          <div class="row align-items-center">
            <div class="col-auto">
              <div class="sp-avatar-lg">
                <img v-if="seller.avatar_url" :src="photoUrl(seller.avatar_url)" :alt="seller.display_name" />
                <span v-else>{{ getInitials(seller.display_name) }}</span>
              </div>
            </div>
            <div class="col">
              <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
                <h4 class="fw-bold mb-0" style="color:var(--fm-h,#F5F0E8);">{{ seller.display_name }}</h4>
                <span v-if="isVerified" class="badge bg-success bg-opacity-25 text-success" style="font-size:11px;">
                <i class="mdi mdi-check-decagram me-1"></i>Verified
              </span>
                <span v-for="role in displayRoles" :key="role" class="badge bg-warning bg-opacity-15 text-warning" style="font-size:10px;">{{ role }}</span>
              </div>
              <div class="d-flex flex-wrap align-items-center gap-3" style="color:var(--fm-m,#A09888);font-size:13px;">
                <span v-if="seller.county_name"><i class="mdi mdi-map-marker me-1"></i>{{ seller.county_name }}<span v-if="seller.sub_county_name">, {{ seller.sub_county_name }}</span></span>
                <span v-if="seller.created_at"><i class="mdi mdi-calendar me-1"></i>Member since {{ memberSince }}</span>
                <span v-if="seller.last_active_at"><i class="mdi mdi-clock-outline me-1"></i>{{ lastActiveText }}</span>
              </div>
            </div>
            <div class="col-12 col-md-auto mt-3 mt-md-0">
              <div class="d-flex gap-2">
                <button class="btn btn-warning btn-sm" @click="contactSeller">
                  <i class="mdi mdi-phone me-1"></i> Contact
                </button>
                <button class="btn btn-outline-warning btn-sm" @click="messageSeller">
                  <i class="mdi mdi-message-text-outline me-1"></i> Message
                </button>
                <button class="btn btn-outline-secondary btn-sm" @click="shareSeller">
                  <i class="mdi mdi-share-variant"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== STATS ROW ===== -->
        <div class="row g-3 mb-4">
          <div class="col-6 col-md-3">
            <div class="sp-stat-card" style="background:var(--fm-bg1,#141414)!important;">
              <div class="sp-stat-icon"><i class="mdi mdi-star" style="color:#f1b44c;"></i></div>
              <div class="sp-stat-value">{{ sellerRating ? Number(sellerRating).toFixed(1) : 'N/A' }}</div>
              <div class="sp-stat-label">Rating</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="sp-stat-card" style="background:var(--fm-bg1,#141414)!important;">
              <div class="sp-stat-icon"><i class="mdi mdi-message-star-outline" style="color:#BFA164;"></i></div>
              <div class="sp-stat-value">{{ totalReviews }}</div>
              <div class="sp-stat-label">Reviews</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="sp-stat-card" style="background:var(--fm-bg1,#141414)!important;">
              <div class="sp-stat-icon"><i class="mdi mdi-tag-multiple-outline" style="color:#50C878;"></i></div>
              <div class="sp-stat-value">{{ activeListings.length }}</div>
              <div class="sp-stat-label">Active Listings</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="sp-stat-card" style="background:var(--fm-bg1,#141414)!important;">
              <div class="sp-stat-icon"><i class="mdi mdi-handshake-outline" style="color:#6CB4EE;"></i></div>
              <div class="sp-stat-value">{{ seller.login_count || 0 }}</div>
              <div class="sp-stat-label">Transactions</div>
            </div>
          </div>
        </div>

        <div class="row">

          <!-- ===== LEFT COLUMN ===== -->
          <div class="col-lg-8">

            <!-- Active Listings -->
            <div class="sp-section">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <h5 class="sp-section-title mb-0"><i class="mdi mdi-store me-2"></i>Active Listings</h5>
                <span v-if="activeListings.length > 0" class="badge bg-warning bg-opacity-15 text-warning">{{ activeListings.length }}</span>
              </div>

              <div v-if="loadingListings" class="text-center py-4">
                <div class="spinner-border spinner-border-sm text-warning"></div>
              </div>

              <div v-else-if="activeListings.length === 0" class="text-center py-4">
                <i class="mdi mdi-package-variant" style="font-size:40px;color:var(--fm-s,#3A3530);"></i>
                <p class="mt-2" style="color:var(--fm-s,#6B6356);font-size:14px;">No active listings at the moment</p>
              </div>

              <div v-else class="row g-3">
                <div class="col-6 col-md-4" v-for="item in activeListings" :key="item.id">
                  <div class="sp-listing-card" style="background:var(--fm-bg1,#141414)!important;" @click="$router.push('/marketplace/listing/' + item.id)">
                    <div class="sp-listing-img">
                      <img v-if="item.primary_photo" :src="photoUrl(item.primary_photo)" :alt="item.title" />
                      <div v-else class="sp-listing-placeholder">
                        <i :class="getSpeciesIcon(item.species_name)" style="font-size:36px;color:rgba(191,161,100,0.3);"></i>
                      </div>
                      <div class="sp-listing-badges">
                        <span v-if="item.is_verified" class="badge bg-success" style="font-size:9px;"><i class="mdi mdi-check-decagram"></i></span>
                        <span v-if="item.is_featured" class="badge bg-warning text-dark" style="font-size:9px;"><i class="mdi mdi-star"></i></span>
                      </div>
                    </div>
                    <div class="sp-listing-body">
                      <div class="sp-listing-title">{{ item.title }}</div>
                      <div class="sp-listing-price">KES {{ formatPrice(item.price) }}</div>
                      <div class="sp-listing-meta">
                        <span v-if="item.species_name">{{ item.species_name }}</span>
                        <span v-if="item.county_name"> · {{ item.county_name }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Reviews Section -->
            <div class="sp-section">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <h5 class="sp-section-title mb-0"><i class="mdi mdi-star-outline me-2"></i>Reviews</h5>
                <span v-if="reviews.length > 0" class="badge bg-warning bg-opacity-15 text-warning">{{ totalReviews }}</span>
              </div>

              <div v-if="loadingReviews" class="text-center py-4">
                <div class="spinner-border spinner-border-sm text-warning"></div>
              </div>

              <div v-else-if="reviews.length === 0" class="text-center py-4">
                <i class="mdi mdi-message-star-outline" style="font-size:40px;color:var(--fm-s,#3A3530);"></i>
                <p class="mt-2" style="color:var(--fm-s,#6B6356);font-size:14px;">No reviews yet</p>
              </div>

              <div v-else>
                <div class="sp-review-item" v-for="(rev, ri) in reviews" :key="'rev-'+ri">
                  <div class="d-flex align-items-start gap-3">
                    <div class="sp-review-avatar">
                      <img v-if="rev.reviewer_avatar" :src="photoUrl(rev.reviewer_avatar)" :alt="rev.reviewer_name" />
                      <span v-else>{{ getInitials(rev.reviewer_name) }}</span>
                    </div>
                    <div class="flex-fill">
                      <div class="d-flex align-items-center justify-content-between flex-wrap mb-1">
                        <div>
                          <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:14px;">{{ rev.reviewer_name }}</span>
                          <span class="ms-2" style="color:var(--fm-s,#6B6356);font-size:12px;">{{ formatDate(rev.created_at) }}</span>
                        </div>
                        <div class="sp-review-stars">
                          <i v-for="s in 5" :key="'s-'+ri+'-'+s" class="mdi" :class="s <= Math.round(rev.overall_rating) ? 'mdi-star text-warning' : 'mdi-star-outline'" style="font-size:14px;"></i>
                        </div>
                      </div>
                      <div v-if="rev.review_title" class="fw-semibold mb-1" style="color:#D4C9B8;font-size:13px;">{{ rev.review_title }}</div>
                      <p style="color:var(--fm-m,#A09888);font-size:13px;margin:0;line-height:1.5;">{{ rev.review_text }}</p>
                      <div v-if="rev.quality_rating || rev.communication_rating || rev.timeliness_rating" class="d-flex flex-wrap gap-3 mt-2" style="font-size:11px;color:var(--fm-s,#6B6356);">
                        <span v-if="rev.quality_rating"><i class="mdi mdi-check-circle me-1"></i>Quality: {{ rev.quality_rating }}/5</span>
                        <span v-if="rev.communication_rating"><i class="mdi mdi-message-text me-1"></i>Comms: {{ rev.communication_rating }}/5</span>
                        <span v-if="rev.timeliness_rating"><i class="mdi mdi-clock me-1"></i>Speed: {{ rev.timeliness_rating }}/5</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- ===== RIGHT COLUMN ===== -->
          <div class="col-lg-4">

            <!-- Rating Breakdown -->
            <div class="sp-sidebar-card" v-if="ratingStats" style="background:var(--fm-bg1,#141414)!important;color:var(--fm-h,#F5F0E8);">
              <h6 class="fw-bold mb-3" style="color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-chart-bar me-2"></i>Rating Breakdown</h6>
              <div class="text-center mb-3">
                <div style="font-size:42px;font-weight:700;color:#f1b44c;">{{ sellerRating ? Number(sellerRating).toFixed(1) : '—' }}</div>
                <div class="sp-stars-row">
                  <i v-for="s in 5" :key="'rs-'+s" class="mdi" :class="s <= Math.round(sellerRating || 0) ? 'mdi-star text-warning' : 'mdi-star-outline'" style="font-size:18px;"></i>
                </div>
                <div style="color:var(--fm-s,#6B6356);font-size:12px;margin-top:4px;">{{ totalReviews }} review{{ totalReviews !== 1 ? 's' : '' }}</div>
              </div>
              <div class="sp-rating-bar" v-for="star in [5,4,3,2,1]" :key="'bar-'+star">
                <span class="sp-bar-label">{{ star }}<i class="mdi mdi-star text-warning" style="font-size:11px;"></i></span>
                <div class="sp-bar-track">
                  <div class="sp-bar-fill" :style="'width:' + getStarPercent(star) + '%'"></div>
                </div>
                <span class="sp-bar-count">{{ getStarCount(star) }}</span>
              </div>
            </div>

            <!-- Seller Info -->
            <div class="sp-sidebar-card" style="background:var(--fm-bg1,#141414)!important;color:var(--fm-h,#F5F0E8);">
              <h6 class="fw-bold mb-3" style="color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-account-details me-2"></i>Seller Information</h6>
              <div class="sp-info-row" v-if="seller.user_type">
                <span class="sp-info-label">Account Type</span>
                <span class="sp-info-value">{{ seller.user_type.charAt(0).toUpperCase() + seller.user_type.slice(1) }}</span>
              </div>
              <div class="sp-info-row" v-if="seller.county_name">
                <span class="sp-info-label">Location</span>
                <span class="sp-info-value">{{ seller.county_name }}<span v-if="seller.sub_county_name">, {{ seller.sub_county_name }}</span></span>
              </div>
              <div class="sp-info-row" v-if="seller.preferred_language">
                <span class="sp-info-label">Language</span>
                <span class="sp-info-value">{{ seller.preferred_language === 'sw' ? 'Kiswahili' : 'English' }}</span>
              </div>
              <div class="sp-info-row">
                <span class="sp-info-label">Member Since</span>
                <span class="sp-info-value">{{ memberSince }}</span>
              </div>
              <div class="sp-info-row" v-if="seller.referral_code">
                <span class="sp-info-label">Referral Code</span>
                <span class="sp-info-value" style="font-family:monospace;color:#BFA164;">{{ seller.referral_code }}</span>
              </div>
            </div>

            <!-- Verifications -->
            <div class="sp-sidebar-card" style="background:var(--fm-bg1,#141414)!important;color:var(--fm-h,#F5F0E8);" v-if="seller.verifications && seller.verifications.length > 0">
              <h6 class="fw-bold mb-3" style="color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-shield-check me-2"></i>Verifications</h6>
              <div v-for="(v, vi) in seller.verifications" :key="'v-'+vi" class="d-flex align-items-center gap-2 mb-2">
                <i class="mdi" :class="v.verification_status === 'verified' ? 'mdi-check-circle text-success' : v.verification_status === 'pending' ? 'mdi-clock-outline text-warning' : 'mdi-close-circle text-danger'" style="font-size:18px;"></i>
                <div>
                  <div style="color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:500;">{{ formatVerificationType(v.verification_type) }}</div>
                  <div style="color:var(--fm-s,#6B6356);font-size:11px;">
                    {{ v.verification_status === 'verified' ? 'Verified' : v.verification_status === 'pending' ? 'Pending review' : 'Rejected' }}
                    <span v-if="v.verified_at"> · {{ formatDate(v.verified_at) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Safety Tips -->
            <div class="sp-sidebar-card" style="background:var(--fm-bg1,#141414)!important;color:var(--fm-h,#F5F0E8);border-color:#2A2520;">
              <h6 class="fw-bold mb-3" style="color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-shield-alert-outline me-2" style="color:#f1b44c;"></i>Safety Tips</h6>
              <ul style="padding-left:18px;margin:0;color:var(--fm-m,#A09888);font-size:12px;line-height:2;">
                <li>Always inspect animals before purchasing</li>
                <li>Use Flockr's M-Pesa escrow for secure payments</li>
                <li>Check seller's verification status and reviews</li>
                <li>Never send money outside the platform</li>
                <li>Report suspicious activity immediately</li>
              </ul>
            </div>

          </div>
        </div>

      </div>

      <!-- ========== MOBILE BOTTOM BAR ========== -->
      <div v-if="seller.id && !loading" class="sp-mobile-bar d-lg-none">
        <button class="btn btn-warning flex-fill" @click="contactSeller">
          <i class="mdi mdi-phone me-1"></i> Call Seller
        </button>
        <button class="btn btn-outline-warning flex-fill" @click="messageSeller">
          <i class="mdi mdi-message-text-outline me-1"></i> Message
        </button>
      </div>

    </div>
    </body>
  </div>
</template>

<!-- ========== STYLES ========== -->
<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
</style>

<style>
html[data-theme="light"] .sp-section,
html[data-theme="light"] .sp-header-card,
html[data-theme="light"] .sp-stat-card,
html[data-theme="light"] .sp-sidebar-card,
html[data-theme="light"] .sp-listing-card,
html[data-theme="light"] .sp-review-item,
html[data-theme="light"] .sp-bar-track,
html[data-theme="light"] .sp-avatar-lg,
html[data-theme="light"] .sp-review-avatar,
html[data-theme="light"] .sp-listing-img {
  background: #FFFFFF !important;
  color: #1A1A1A !important;
  border-color: #E8E2DA !important;
}

html[data-theme="light"] .sp-section-title,
html[data-theme="light"] .sp-listing-title,
html[data-theme="light"] .sp-stat-value,
html[data-theme="light"] .sp-info-value {
  color: #1A1A1A !important;
}

html[data-theme="light"] .sp-stat-label,
html[data-theme="light"] .sp-info-label,
html[data-theme="light"] .sp-listing-meta,
html[data-theme="light"] .sp-bar-label,
html[data-theme="light"] .sp-bar-count {
  color: #777 !important;
}

html[data-theme="light"] .sp-mobile-bar .btn-outline-warning {
  border-color: #BFA164 !important;
  color: #BFA164 !important;
}
</style>
<style scoped>
.seller-profile-root { background:var(--fm-bg2,#0F0F0F)!important; min-height:100vh; color:var(--fm-h,#F5F0E8)!important; }

.sp-topbar {
  position:sticky; top:0; z-index:100; padding:12px 0;
  background:var(--fm-bg1,#141414)!important; backdrop-filter:blur(10px);
  border-bottom:1px solid var(--fm-br,#1E1E1E);
}

.sp-container { padding-top:80px; padding-bottom:100px; max-width:1140px; }

/* Header Card */
.sp-header-card {
  background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#1E1E1E); border-radius:12px;
  padding:24px; margin-bottom:24px;
}
.sp-avatar-lg {
  width:80px; height:80px; border-radius:50%; overflow:hidden;
  background:var(--fm-bg3,#1E1E1E)!important; display:flex; align-items:center; justify-content:center;
  font-size:28px; font-weight:700; color:#BFA164!important; border:3px solid #2A2520;
}
.sp-avatar-lg img { width:100%; height:100%; object-fit:cover; }

/* Stat Cards */
.sp-stat-card {
  background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#1E1E1E); border-radius:10px;
  padding:16px; text-align:center;
}
.sp-stat-icon { margin-bottom:6px; font-size:22px; }
.sp-stat-value { font-size:22px; font-weight:700; color:var(--fm-h,#F5F0E8)!important; }
.sp-stat-label { font-size:12px; color:var(--fm-s,#6B6356)!important; margin-top:2px; }

/* Sections */
.sp-section { margin-bottom:32px; }
.sp-section-title { font-size:16px; font-weight:700; color:var(--fm-h,#F5F0E8)!important; }

/* Listing Cards */
.sp-listing-card {
  background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#1E1E1E); border-radius:10px;
  overflow:hidden; cursor:pointer; transition:all 0.2s ease;
}
.sp-listing-card:hover { border-color:#BFA164; transform:translateY(-2px); }
.sp-listing-img {
  position:relative; height:140px; overflow:hidden; background:var(--fm-bg2,#0F0F0F)!important;
}
.sp-listing-img img { width:100%; height:100%; object-fit:cover; }
.sp-listing-placeholder {
  width:100%; height:100%; display:flex; align-items:center; justify-content:center;
}
.sp-listing-badges {
  position:absolute; top:8px; left:8px; display:flex; gap:4px;
}
.sp-listing-body { padding:12px; }
.sp-listing-title {
  font-size:13px; font-weight:600; color:var(--fm-h,#F5F0E8)!important; margin-bottom:4px;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.sp-listing-price { font-size:15px; font-weight:700; color:#BFA164!important; margin-bottom:4px; }
.sp-listing-meta { font-size:11px; color:var(--fm-s,#6B6356)!important; }

/* Reviews */
.sp-review-item {
  padding:16px 0; border-bottom:1px solid var(--fm-br,#1E1E1E);
}
.sp-review-item:last-child { border-bottom:none; }
.sp-review-avatar {
  width:40px; height:40px; border-radius:50%; overflow:hidden; flex-shrink:0;
  background:var(--fm-bg3,#1E1E1E)!important; display:flex; align-items:center; justify-content:center;
  font-size:14px; font-weight:700; color:#BFA164!important;
}
.sp-review-avatar img { width:100%; height:100%; object-fit:cover; }

/* Sidebar Cards */
.sp-sidebar-card {
  background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#1E1E1E); border-radius:10px;
  padding:20px; margin-bottom:16px;
}

/* Rating Breakdown */
.sp-stars-row { display:flex; justify-content:center; gap:2px; }
.sp-rating-bar {
  display:flex; align-items:center; gap:8px; margin-bottom:6px;
}
.sp-bar-label { width:30px; text-align:right; font-size:12px; color:var(--fm-m,#A09888)!important; }
.sp-bar-track {
  flex:1; height:8px; background:var(--fm-bg3,#1E1E1E)!important; border-radius:4px; overflow:hidden;
}
.sp-bar-fill {
  height:100%; background:linear-gradient(90deg, #BFA164, #f1b44c); border-radius:4px;
  transition:width 0.4s ease;
}
.sp-bar-count { width:24px; font-size:11px; color:var(--fm-s,#6B6356)!important; }

/* Info Rows */
.sp-info-row {
  display:flex; justify-content:space-between; align-items:center;
  padding:8px 0; border-bottom:1px solid var(--fm-br,#1A1A1A);
}
.sp-info-row:last-child { border-bottom:none; }
.sp-info-label { font-size:13px; color:var(--fm-s,#6B6356)!important; }
.sp-info-value { font-size:13px; color:#D4C9B8!important; font-weight:500; }

/* Mobile Bottom Bar */
.sp-mobile-bar {
  position:fixed; bottom:0; left:0; right:0; z-index:100;
  background:rgba(20,20,20,0.98)!important; backdrop-filter:blur(10px);
  border-top:1px solid #1E1E1E; padding:12px 16px;
  display:flex; gap:10px;
}

@media (max-width: 768px) {
  .sp-container { padding-top:70px; padding-bottom:120px; }
  .sp-header-card { padding:16px; }
  .sp-avatar-lg { width:60px; height:60px; font-size:22px; }
  .sp-listing-img { height:110px; }
}
</style>

<style>
html[data-theme="light"] body,
html[data-theme="light"] html,
html[data-theme="light"] #layout-wrapper,
html[data-theme="light"] .main-content,
html[data-theme="light"] .page-content,
html[data-theme="light"] .seller-profile-root,
html[data-theme="light"] #app {
  background: #F7F5F2 !important;
  color: #1A1A1A !important;
}

html[data-theme="dark"] body,
html[data-theme="dark"] html,
html[data-theme="dark"] #layout-wrapper,
html[data-theme="dark"] .main-content,
html[data-theme="dark"] .page-content,
html[data-theme="dark"] .seller-profile-root,
html[data-theme="dark"] #app {
  background: #0A0A0A !important;
  color: #F5F0E8 !important;
}

html[data-theme="light"] .sp-topbar {
  background: #FFFFFF !important;
  border-bottom-color: #E8E2DA !important;
}

html[data-theme="light"] .sp-mobile-bar {
  background: rgba(255,255,255,0.98) !important;
  border-top-color: #E8E2DA !important;
}

html[data-theme="light"] .text-muted {
  color: #777 !important;
}

html[data-theme="light"] .text-dark {
  color: #1A1A1A !important;
}

html[data-theme="dark"] .sp-topbar {
  background: rgba(10,10,10,0.95) !important;
}

html[data-theme="dark"] .sp-mobile-bar {
  background: rgba(20,20,20,0.98) !important;
  border-top-color: #1E1E1E !important;
}
</style>

<!-- ========== SCRIPT ========== -->
<script>
/* eslint-disable */
import { executeJson, flocker, uploads } from "@/api";

export default {
  name: "SellerProfile",
  data: function() {
    return {
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
      themeObserver: null,

      loading: true,
      loadingListings: true,
      loadingReviews: true,
      seller: {},
      activeListings: [],
      reviews: [],
      ratingStats: null
    };
  },
  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    isVerified: function() {
      if (!this.seller.verifications) return false;
      return this.seller.verifications.some(function(v) { return v.verification_status === 'verified'; });
    },
    displayRoles: function() {
      if (!this.seller.roles) return [];
      return this.seller.roles.map(function(r) {
        var name = r.role || r;
        return name.charAt(0).toUpperCase() + name.slice(1);
      }).filter(function(r) { return r !== 'Buyer'; });
    },
    sellerRating: function() {
      if (!this.ratingStats) return null;
      return this.ratingStats.avg_overall || null;
    },
    totalReviews: function() {
      if (!this.ratingStats) return 0;
      return parseInt(this.ratingStats.total_reviews) || 0;
    },
    memberSince: function() {
      if (!this.seller.created_at) return '';
      var d = new Date(this.seller.created_at);
      var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return months[d.getMonth()] + ' ' + d.getFullYear();
    },
    lastActiveText: function() {
      if (!this.seller.last_active_at) return '';
      var now = new Date();
      var last = new Date(this.seller.last_active_at);
      var diff = Math.floor((now - last) / (1000 * 60));
      if (diff < 5) return 'Online now';
      if (diff < 60) return diff + 'm ago';
      if (diff < 1440) return Math.floor(diff / 60) + 'h ago';
      if (diff < 10080) return Math.floor(diff / 1440) + 'd ago';
      return this.formatDate(this.seller.last_active_at);
    }
  },
  watch: {
    '$route.params.id': function(newId) {
      if (newId) this.loadProfile(newId);
    }
  },
  mounted: function() {
    this.syncTheme();
    this.themeObserver = new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    var sellerId = this.$route.params.id;
    if (sellerId) {
      this.loadProfile(sellerId);
    }
  },
  beforeUnmount: function() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },
  methods: {
    syncTheme: function() {
      var t = document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark';
      window.__flockrSetTheme(t);
      this.darkMode = t !== 'light';

      if (document.body) {
        document.body.setAttribute('data-sidebar', this.darkMode ? 'dark' : 'light');
        document.body.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        document.body.style.backgroundColor = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        document.body.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }

      var lw = document.getElementById('layout-wrapper');
      if (lw) {
        lw.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        lw.style.backgroundColor = this.darkMode ? '#0A0A0A' : '#F7F5F2';
      }

      var mc = document.querySelector('.main-content');
      if (mc) {
        mc.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        mc.style.backgroundColor = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        mc.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }

      var pc = document.querySelector('.page-content');
      if (pc) {
        pc.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        pc.style.backgroundColor = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        pc.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
    },
    photoUrl(p){if(!p)return'';if(p.startsWith&&p.startsWith('http'))return p;return uploads+String(p).replace('uploads/','')},


    // ============ LOAD ============

    async loadProfile(sellerId) {
      this.loading = true;
      this.seller = {};
      this.activeListings = [];
      this.reviews = [];
      this.ratingStats = null;
      window.scrollTo({ top: 0 });

      try {
        await this.fetchSellerProfile(sellerId);
        if (this.seller.id) {
          this.fetchSellerListings(sellerId);
          this.fetchSellerReviews(sellerId);
        }
      } catch (e) {
        console.error('Error loading profile:', e);
      } finally {
        this.loading = false;
      }
    },

    // ============ API CALLS ============

    async fetchSellerProfile(sellerId) {
      try {
        var response = await executeJson({
          function: "getUserProfile",
          userId: sellerId
        }, flocker);
        if (response.data.success) {
          this.seller = response.data.data.user || {};
          var stats = this.seller.rating_stats || [];
          this.ratingStats = stats.find(function(s) { return s.role_context === 'seller'; }) || stats[0] || null;
        }
      } catch (e) {
        console.error('Error fetching seller profile:', e);
      }
    },

    async fetchSellerListings(sellerId) {
      this.loadingListings = true;
      try {
        var response = await executeJson({
          function: "getMyListings",
          sellerId: sellerId,
          status: "active"
        }, flocker);
        if (response.data.success) {
          this.activeListings = response.data.data.listings || [];
        }
      } catch (e) {
        console.error('Error fetching listings:', e);
      } finally {
        this.loadingListings = false;
      }
    },

    async fetchSellerReviews(sellerId) {
      this.loadingReviews = true;
      try {
        var response = await executeJson({
          function: "getUserReviews",
          userId: sellerId
        }, flocker);
        if (response.data.success) {
          this.reviews = response.data.data.reviews || [];
          if (!this.ratingStats && response.data.data.stats) {
            var stats = response.data.data.stats || [];
            this.ratingStats = stats.find(function(s) { return s.role_context === 'seller'; }) || stats[0] || null;
          }
        }
      } catch (e) {
        console.error('Error fetching reviews:', e);
      } finally {
        this.loadingReviews = false;
      }
    },

    // ============ ACTIONS ============

    contactSeller: function() {
      if (this.seller.phone) {
        window.open('tel:' + this.seller.phone);
      } else {
        alert('Seller phone is not available.');
      }
    },

    messageSeller: function() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) {
        this.$router.push('/login');
        return;
      }
      this.$router.push('/messages?to=' + this.seller.id);
    },

    shareSeller: function() {
      var url = window.location.href;
      var name = this.seller.display_name || 'Seller';
      if (navigator.share) {
        navigator.share({
          title: name + ' on Flockr',
          text: 'Check out ' + name + '\'s profile on Flockr marketplace',
          url: url
        }).catch(function() {});
      } else {
        navigator.clipboard.writeText(url).then(function() {
          alert('Profile link copied!');
        }).catch(function() {
          prompt('Copy this link:', url);
        });
      }
    },

    // ============ RATING HELPERS ============

    getStarCount: function(star) {
      if (!this.ratingStats) return 0;
      var map = { 5: 'five_star_count', 4: 'four_star_count', 3: 'three_star_count', 2: 'two_star_count', 1: 'one_star_count' };
      return parseInt(this.ratingStats[map[star]]) || 0;
    },

    getStarPercent: function(star) {
      if (!this.totalReviews) return 0;
      return Math.round((this.getStarCount(star) / this.totalReviews) * 100);
    },

    // ============ FORMATTERS ============

    formatVerificationType: function(type) {
      if (!type) return 'Unknown';
      var map = {
        'national_id': 'National ID', 'passport': 'Passport',
        'drivers_license': "Driver's License", 'kra_pin': 'KRA PIN',
        'business_registration': 'Business Registration',
        'kvb_license': 'KVB License', 'phone': 'Phone Number'
      };
      return map[type] || type.replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
    },

    formatPrice: function(amount) {
      if (!amount) return '0';
      return Number(amount).toLocaleString('en-KE');
    },

    formatDate: function(dateStr) {
      if (!dateStr) return '';
      var d = new Date(dateStr);
      return d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
    },

    getInitials: function(name) {
      if (!name) return '?';
      return name.split(' ').map(function(w) { return w[0]; }).join('').substring(0, 2).toUpperCase();
    },

    getSpeciesIcon: function(species) {
      if (!species) return 'mdi mdi-help-circle-outline';
      var s = species.toLowerCase();
      if (s.indexOf('cattle') >= 0 || s.indexOf('cow') >= 0 || s.indexOf('bull') >= 0) return 'mdi mdi-cow';
      if (s.indexOf('goat') >= 0) return 'mdi mdi-unicode';
      if (s.indexOf('sheep') >= 0) return 'mdi mdi-sheep';
      if (s.indexOf('chicken') >= 0 || s.indexOf('poultry') >= 0) return 'mdi mdi-food-drumstick';
      if (s.indexOf('pig') >= 0 || s.indexOf('swine') >= 0) return 'mdi mdi-pig-variant-outline';
      if (s.indexOf('rabbit') >= 0) return 'mdi mdi-rabbit';
      if (s.indexOf('donkey') >= 0) return 'mdi mdi-horse-variant';
      if (s.indexOf('camel') >= 0) return 'mdi mdi-image-filter-hdr';
      if (s.indexOf('fish') >= 0 || s.indexOf('tilapia') >= 0) return 'mdi mdi-fish';
      if (s.indexOf('duck') >= 0 || s.indexOf('turkey') >= 0) return 'mdi mdi-duck';
      if (s.indexOf('bee') >= 0) return 'mdi mdi-bee';
      return 'mdi mdi-paw';
    }
  }
};
</script>
